import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

function timeAgo(iso) {
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const CATEGORY_IMG = {
  "Markets": "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?crop=entropy&cs=srgb&fm=jpg&q=70&w=200",
  "Commodities": "https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?crop=entropy&cs=srgb&fm=jpg&q=70&w=200",
  "Real Estate": "https://images.unsplash.com/photo-1710582308582-55cc0c461c4e?crop=entropy&cs=srgb&fm=jpg&q=70&w=200",
  "Crypto": "https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&q=70&w=200",
  "IPO": "https://images.unsplash.com/photo-1554224154-26032fced8bd?crop=entropy&cs=srgb&fm=jpg&q=70&w=200",
  "Economy": "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?crop=entropy&cs=srgb&fm=jpg&q=70&w=200",
};

/** "Latest on the Street" — compact 3-row news feed with thumbnails. */
export default function LatestOnStreet() {
  const { data, isLoading } = useQuery({
    queryKey: ["latest-street"],
    queryFn: async () => (await api.get("/news?limit=6")).data,
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
  const items = (data?.data || []).slice(0, 5);

  return (
    <section className="ns-card p-5" data-testid="latest-on-street">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <span className="ns-rule" />
          <h2 className="font-heading text-xl text-[#1A1A1A]">Latest on the Street</h2>
        </div>
        <Link to="/news" className="text-xs font-semibold text-[#D91E2A] hover:text-[#B8141F]" data-testid="latest-view-all">
          View all →
        </Link>
      </header>

      {isLoading && <div className="text-sm text-[#8C8D90] font-mono-fin py-6">Loading…</div>}

      <ul className="divide-y divide-[#E2DDD3]">
        {items.map(n => {
          const img = n.image || CATEGORY_IMG[n.category] || CATEGORY_IMG.Markets;
          return (
            <li key={n.id}>
              <Link to={`/news/${n.slug || n.id}`} className="flex gap-4 py-3 group" data-testid={`latest-item-${n.id}`}>
                <img src={img} alt={n.title || ""} className="w-20 h-20 object-cover rounded flex-shrink-0 bg-[#F0ECE4]" loading="lazy" onError={(e) => { const f = CATEGORY_IMG[n.category] || CATEGORY_IMG.Markets; if (e.currentTarget.src !== f) e.currentTarget.src = f; }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="ns-eyebrow">{n.category || "Markets"}</span>
                    <span className="text-[11px] text-[#8C8D90] font-mono-fin">· {timeAgo(n.published)} ago</span>
                  </div>
                  <h3 className="font-heading text-base font-bold leading-tight text-[#1A1A1A] group-hover:text-[#D91E2A] line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="text-xs text-[#6B6C6F] mt-1 line-clamp-2">
                    {(n.summary || "").replace(/<[^>]*>/g, "").slice(0, 150)}
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
